/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback, useEffect, useLayoutEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import isEmpty from 'lodash.isempty';
// eslint-disable-next-line no-restricted-imports
import {useRouter} from 'next/router';
import PersistentMapHost from '@ion/applications/src/components/persistent/PersistentMapHost';
import {removeAllUIComponents} from '@ion/dls/src/maps/helpers/MapViewUtils';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {TrackingMode} from '@ion/dls/src/types/types';
import {Route} from '@/routes/RouteDefinitions';
import {historyPropsSelector} from '@/store/selectors/reports-selectors';
import {trackingModeSelector} from '@/store/selectors/tracking-selectors';
import AssetHistoryMap from './AssetHistoryMap';

export const HISTORY_MAP_CONTAINER_ID = 'history-map-v2';

/**
 * A wrapper component for `<AssetHistoryMap />` that ensures it persists
 * in the DOM and remains mounted even after navigating away from `/history`.
 *
 * It uses the reusable `PersistentMapHost` to manage lifecycle and conditional
 * visibility based on route matching.
 *
 * This improves performance and avoids reinitializing the tracking map when 
 * navigating back and forth between routes.
 *
 * ```typescript
 * // Use inside your page or layout component
 * import PersistentHistoryMapHost from './PersistentHistoryMapHost';
 * 
 * const App = () => (
 *   <>
 *     <PersistentHistoryMapHost />
 *     <MainLayout />
 *   </>
 * );
 * 
 * ```
 */
const PersistentHistoryMapHost = () => {
  const trackingMode = useSelector(trackingModeSelector);
  const route        = useRouter();
  const path         = route?.route;
  const mapViewRef   = useRef<__esri.MapView>(null);
  const props        = useSelector(historyPropsSelector) ?? {};
  const shouldShow   = (trackingMode === TrackingMode.History && (path === `/${Route.TRACKING2}`));
  const {isCurrent}  = useThemeContext();
  // the element the history map draws into, while it is detached ...
  const detachedContainerRef = useRef<HTMLDivElement>(null);

  // current theme: the hidden history map is detached from its element,
  // releasing its drawing surface (and the GPU memory behind it) while the
  // live map shows - it keeps its map, layers and state, and is attached
  // again before anything on it renders ...
  useLayoutEffect(() => {
    const view = mapViewRef.current;
    if (shouldShow && view && detachedContainerRef.current) {
      view.container             = detachedContainerRef.current;
      detachedContainerRef.current = null;
    }
  }, [shouldShow]);

  useEffect(() => {
    const view = mapViewRef.current;
    if (!shouldShow && view) {
      removeAllUIComponents(view);
      if (isCurrent && view.container) {
        detachedContainerRef.current = view.container as HTMLDivElement;
        view.container               = null;
      }
    }
  }, [shouldShow]);
  
  const onSetView = useCallback((view: __esri.MapView) => {
    mapViewRef.current = view;
  }, []);
 
  if (!isEmpty(props)) {
    return (
      <PersistentMapHost show={shouldShow}
        containerId={HISTORY_MAP_CONTAINER_ID}>
        <AssetHistoryMap {...props} onSetView={onSetView}
          renderMapWidgets={shouldShow} />
      </PersistentMapHost>
    );
  }
};

export default memo(PersistentHistoryMapHost);
